/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {TranslateService} from '@ngx-translate/core';

/**
 * Utils for payrolls and payroll statuses.
 */
export class Payrolls {

  /**
   * Translate a payroll status code to a display name
   *
   * @returns {string} the display name
   */
  public static getDisplayNameForStatus(status: string, translate: TranslateService): string {
    let msg: string;
    switch (status) {
      case 'SENT':
        msg = translate.instant('COMMON.sent');
        break;
      case 'DONE':
        msg = translate.instant('COMMON.done');
        break;
      case 'REJECTED':
        msg = translate.instant('COMMON.rejected');
        break;
      case 'DISCARDED':
        msg = translate.instant('COMMON.discarded');
        break;
      case 'TODO':
      case 'UPLOADED':
        msg = translate.instant('COMMON.todo');
        break;
      case 'ALERT':
        msg = translate.instant('COMMON.alert');
        break;
      case 'ERROR':
        msg = translate.instant('COMMON.error');
        break;
      case 'SCHEDULED' :
      case 'SENDED':
        msg = translate.instant('COMMON.scheduled');
        break;
      case 'AGAIN' :
        msg = translate.instant('COMMON.again');
        break;
      case 'FROZEN':
        return translate.instant('COMMON.frozen');
      case 'CLOSED':
        msg = translate.instant('CHARTS.PAYROLL.closed');
        break;
      case 'NOT_READY':
        msg = translate.instant('PAYROLL_ONGOING.not_ready');
        break;
      case 'READY':
        msg = translate.instant('PAYROLL_ONGOING.ready');
        break;
      case 'VALIDATED':
        msg = translate.instant('PAYROLL_ONGOING.validated');
        break;
      case 'IN_PROGRESS':
        msg = translate.instant('POSTPAYROLL.PREPARE.inprogress');
        break;
      case 'ONGOING':
      case 'PAYSLIP_IN_PROGRESS':
      case 'CALCULATION_DONE':
      case 'STORAGE_IN_PROGRESS':
        msg = translate.instant('COMMON.ongoing');
        break;
      case 'DRAFT':
        msg = translate.instant('COMMON.draft');
        break;
      case 'IN_TRANSIT':
        msg = translate.instant('COMMON.in_transit');
        break;
      case 'UPLOADING':
        msg = translate.instant('COMMON.uploading');
        break;
      case 'DELETED':
        msg = translate.instant('COMMON.deleted');
        break;
      case 'FAILED':
        msg = translate.instant('COMMON.failed');
        break;
      default:
        msg = '';
        break;
    }

    return msg;
  }

  /**
   * Get the UI color for a status code
   * @param status
   * @returns {function(): string}
   */
  public static getColorForStatus(status: string): string {
    return (() => {
      let color: string;
      // SD Worx Ignite-derived colors (kept semantic per status).
      switch (status) {
        case 'DONE':
          color = '#f3b01d'; // ig warning amber
          break;
        case 'TODO':
          color = '#006dd8'; // ig primary blue (pending)
          break;
        case 'ALERT':
          color = '#e90040'; // ig danger red
          break;
        case 'ERROR':
          color = '#737476'; // ig text-soft grey
          break;
        case 'FROZEN':
          color = '#007900'; // ig success green
          break;
        case 'SCHEDULED' :
          color = '#006aff'; // ig info blue
          break;
        case 'AGAIN' :
          color = '#005bbf'; // ig primary-120 deep blue
          break;
        case 'FIXED':
          color = '#cf0038'; // ig danger-120 deep red
          break;
        case 'CLOSED':
          color = '#007900'; // ig success green
          break;
        case 'NOT_READY':
          color = '#001c52'; // ig primary-strong navy
          break;
        case 'READY':
          color = '#005cf2'; // ig info-120 vivid blue
          break;
        case 'VALIDATED':
          color = '#007900'; // ig success green
          break;
        case 'ONGOING':
        case 'SENDED':
        case 'PAYSLIP_IN_PROGRESS':
        case 'CALCULATION_DONE':
        case 'STORAGE_IN_PROGRESS':
          color = '#005bbf'; // ig primary-120 (in progress)
          break;
        default:
          color = '#fff';
          break;
      }

      return color;
    })();
  }

  public static getDisplayNameForEventType(eventType: string, translate: TranslateService): string {
    return (() => {
      let msg: string;
      switch (eventType) {
        // Critical errors
        case 'ALERT':
          msg = translate.instant('DROPDOWN.Alert');
          break;
        case 'ERROR_TECHNIC':
          msg = translate.instant('DROPDOWN.Error');
          break;
        // Other errors
        case 'TAG':
          msg = 'Tag';
          break;
        case 'INFORMATION':
          msg = translate.instant('DROPDOWN.Information');
          break;
        case 'WARNING':
          msg = translate.instant('DROPDOWN.Warning');
          break;
        default:
          msg = '';
          break;
      }

      return msg;
    })();
  }

  public static getColorForEventType(eventType: string): string {
    return (() => {
      let color: string;
      // SD Worx Ignite-derived colors (kept semantic per event type).
      switch (eventType) {
        // Critical errors
        case 'ALERT':
          color = '#e90040'; // ig danger red
          break;
        case 'ERROR_TECHNIC':
          color = '#737476'; // ig text-soft grey
          break;
        // Other errors
        case 'INFORMATION':
          color = '#006aff'; // ig info blue
          break;
        case 'WARNING':
          color = '#f3b01d'; // ig warning amber
          break;
        case 'TAG':
          color = '#001c52'; // ig primary-strong navy
          break;
        default:
          color = '#fff';
          break;
      }

      return color;
    })();
  }

  public static getCSSClassForEventType(eventType: string): string {
    return (() => {
      let msg: string;
      switch (eventType) {
        case 'ALERT':
          msg = 'text-danger';
          break;
        case 'ERROR_TECHNIC':
          msg = 'text-black';
          break;
        // Other errors
        case 'INFORMATION':
          msg = 'text-success';
          break;
        case 'WARNING':
          msg = 'text-warn';
          break;
        default:
          msg = '';
          break;
      }

      return msg;
    })();
  }

  /**
   * Comparison function that compares event names according to their respective criticality.
   * Unknown event names have the lower criticality.
   *
   * @example
   * ['ALERT', 'WARNING', 'TAG', 'FOO', 'ERROR_TECHNIC', 'INFORMATION'].sort(Payrolls.compareEventTypeByCriticality)
   * // Returns ['ERROR_TECHNIC', 'ALERT', 'WARNING', 'INFORMATION', 'TAG', 'FOO']
   *
   * @param eventA the first event to compare
   * @param eventB the second event to compare
   * @returns {number} comparison result (as expected by Array#sort for example).
   */
  public static compareEventTypeByCriticality(eventA: string, eventB: string): number {
    const levels = {
      'TAG': 0,
      'INFORMATION': 1,
      'WARNING': 2,
      'ALERT': 3,
      'ERROR_TECHNIC': 4
    };
    const levelA = levels[eventA] || -1,
      levelB = levels[eventB] || -1;
    return levelB - levelA;
  }
}
